import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Apple, Plus, Trash2, Pencil, Calendar, AlertCircle, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { exportFoodLogsToPDF } from '../utils/pdfExport';
import TimelineButton from '../components/TimelineButton';
import { todayLocal, formatLocalDate } from '../utils/dateUtils';

export const FoodTracker = () => {
  const { API, token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [formData, setFormData] = useState({
    date: todayLocal(),
    meal_type: 'breakfast',
    foods: [],
    reactions: [],
    notes: ''
  });
  const [foodInput, setFoodInput] = useState('');
  const [reactionInput, setReactionInput] = useState('');

  const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
  const commonReactions = ['None', 'Mild discomfort', 'Headache', 'Hives', 'Stomach pain', 'Fatigue', 'Mood change', 'Congestion'];

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/food-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load food logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.foods.length === 0) {
      toast.error('Please add at least one food item');
      return;
    }

    try {
      if (editingLog) {
        await axios.put(`${API}/food-logs/${editingLog.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Food log updated');
      } else {
        await axios.post(`${API}/food-logs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Food log added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save log');
    }
  };

  const startEdit = (log) => {
    setEditingLog(log);
    setFormData({
      date: log.date,
      meal_type: log.meal_type,
      foods: log.foods || [],
      reactions: log.reactions || [],
      notes: log.notes || ''
    });
    setDialogOpen(true);
  };

  const deleteLog = async (id) => {
    if (!window.confirm('Are you sure you want to delete this log?')) return;
    try {
      await axios.delete(`${API}/food-logs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Log deleted');
      fetchLogs();
    } catch (error) {
      toast.error('Failed to delete log');
    }
  };

  const resetForm = () => {
    setFormData({
      date: todayLocal(),
      meal_type: 'breakfast',
      foods: [],
      reactions: [],
      notes: ''
    });
    setEditingLog(null);
    setFoodInput('');
    setReactionInput('');
  };

  const addFood = (food) => {
    if (food && !formData.foods.includes(food)) {
      setFormData({ ...formData, foods: [...formData.foods, food] });
      setFoodInput('');
    }
  };

  const removeFood = (food) => {
    setFormData({ ...formData, foods: formData.foods.filter(f => f !== food) });
  };

  const addReaction = (reaction) => {
    if (reaction && !formData.reactions.includes(reaction)) {
      setFormData({ ...formData, reactions: [...formData.reactions, reaction] });
      setReactionInput('');
    }
  };

  const removeReaction = (reaction) => {
    setFormData({ ...formData, reactions: formData.reactions.filter(r => r !== reaction) });
  };

  return (
    <div className="space-y-8" data-testid="food-tracker">
      {/* Header */}
      <div className="bg-gradient-to-br from-secondary/20 to-primary/10 rounded-xl p-8 md:p-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <Apple className="w-12 h-12 text-primary" strokeWidth={1.5} />
              <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Food Tracker
              </h1>
            </div>
            <p className="text-lg text-[#57534E]">
              Track meals and identify food sensitivities and triggers
            </p>
          </div>
          <div className="flex gap-3 items-center">
            <TimelineButton />
            <Button
              variant="outline"
              onClick={() => exportFoodLogsToPDF(logs)}
              disabled={logs.length === 0}
              className="rounded-full px-5"
              data-testid="export-food-pdf"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-6 font-bold shadow-sm transition-transform hover:scale-[1.02]"
                  data-testid="add-food-log-button"
                >
                  <Plus className="w-5 h-5 mr-2" />
                Log Meal
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-nunito font-bold">{editingLog ? 'Edit Food Entry' : 'Log Food Entry'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="food-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="meal_type">Meal Type</Label>
                  <select
                    id="meal_type"
                    value={formData.meal_type}
                    onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
                    className="w-full h-12 px-4 bg-stone-50 border border-stone-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="meal-type-select"
                  >
                    {mealTypes.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Foods Eaten</Label>
                  <div className="flex gap-2">
                    <Input
                      value={foodInput}
                      onChange={(e) => setFoodInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFood(foodInput))}
                      placeholder="Type food and press Enter"
                      data-testid="food-input"
                    />
                    <Button
                      type="button"
                      onClick={() => addFood(foodInput)}
                      className="bg-primary text-white"
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.foods.map(food => (
                      <span key={food} className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-2">
                        {food}
                        <button type="button" onClick={() => removeFood(food)} className="hover:text-primary/70">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Reactions (if any)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={reactionInput}
                      onChange={(e) => setReactionInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addReaction(reactionInput))}
                      placeholder="Type reaction"
                      data-testid="reaction-input"
                    />
                    <Button
                      type="button"
                      onClick={() => addReaction(reactionInput)}
                      className="bg-primary text-white"
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {commonReactions.map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => addReaction(r)}
                        className="px-3 py-1 bg-stone-100 hover:bg-accent/20 text-sm rounded-full"
                      >
                        + {r}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.reactions.map(r => (
                      <span key={r} className="px-3 py-1 bg-accent/20 text-accent rounded-full text-sm flex items-center gap-2">
                        {r}
                        <button type="button" onClick={() => removeReaction(r)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    placeholder="Additional observations..."
                    data-testid="food-notes-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-bold"
                  data-testid="save-food-log-button"
                >
                  {editingLog ? 'Update Food Log' : 'Save Food Log'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      {/* Food Logs List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-[#A8A29E]">Loading food logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
          <Apple className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <p className="text-[#57534E] text-lg mb-4">No food logs yet</p>
          <p className="text-[#A8A29E] mb-6">Start tracking to identify sensitivities and patterns</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3"
          >
            Add First Entry
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {logs.map((log) => (
            <Card key={log.id} className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-4">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span className="font-nunito font-bold text-lg text-[#2D2D2D]">
                      {formatLocalDate(log.date)}
                    </span>
                    <span className="px-4 py-1 bg-secondary text-secondary-foreground rounded-full text-sm font-semibold">
                      {log.meal_type.charAt(0).toUpperCase() + log.meal_type.slice(1)}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-[#A8A29E]">Foods:</p>
                    <div className="flex flex-wrap gap-2">
                      {log.foods.map((food, i) => (
                        <span key={i} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                          {food}
                        </span>
                      ))}
                    </div>
                  </div>

                  {log.reactions.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-[#A8A29E] flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Reactions:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {log.reactions.map((reaction, i) => (
                          <span key={i} className="px-3 py-1 bg-accent/10 text-accent rounded-full text-sm">
                            {reaction}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {log.notes && (
                    <p className="text-[#57534E] text-sm italic">{log.notes}</p>
                  )}
                </div>

                <div className="flex items-center gap-1 ml-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => startEdit(log)}
                    className="text-primary hover:text-primary/80"
                    data-testid={`edit-food-log-${log.id}`}
                  >
                    <Pencil className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteLog(log.id)}
                    className="text-accent hover:text-accent/80"
                    data-testid={`delete-food-log-${log.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FoodTracker;
